import { z } from 'zod';

import type { AuthorKind } from './artifacts.ts';
import {
  BRIEF_BLOCKS,
  type BriefBlock,
  type BriefContent,
  MAX_BRIEF_HEADLINE_CHARS,
  MAX_BRIEF_LIST_ITEMS,
  briefContentSchema,
  isPermutationOf,
  richTextChips,
  sameRichText,
} from './brief.ts';
import {
  MAX_BRIEF_PROSE_INPUT_CHARS,
  MAX_RICH_TEXT_SEGMENTS,
  type RichText,
  type RichTextSegment,
  briefToken,
  gatedBriefProse,
  richTextSchema,
} from './brief-counts.ts';
import { canonicalJson } from './canonical-json.ts';
import { DomainError } from './errors.ts';
import type { ArtifactVersionReason, BriefVersionReason } from './version-reasons.ts';

/**
 * A Brief's framing: what its owner may change, and how Sigora's revisions keep it.
 *
 * The researcher owns three things and nothing else — the Verdict headline's words, which summary
 * bullets show, and the order of the recommendations. Each is one edit (`BriefEdit`), and an edit
 * can say nothing else: rewording sends only words, one run per gap around the headline's counts,
 * and the headline is rebuilt around the counts already stored, so no count, total, option wording
 * or other block can be sent at all. The words pass the gate the synthesis's own prose passes.
 *
 * A Sigora revision recomposes the Brief and keeps the researcher's framing wherever what it framed
 * is still there (`rebaseBriefFraming`), and says, in codes, where it is not.
 */

const sameOrder = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((id, index) => id === right[index]);

/**
 * The words of a headline around its counts, totals and option wordings: one run before, between
 * and after each, so a headline with `n` of them has `n + 1` runs, any of which may be empty. What
 * the researcher edits, and what an edit sends back.
 */
export const headlineWordingSlots = (headline: RichText): string[] => {
  const slots: string[] = [];
  let words = '';
  for (const segment of headline) {
    if (segment.t === 'text') {
      words += segment.text;
    } else {
      slots.push(words);
      words = '';
    }
  }
  slots.push(words);

  return slots;
};

/** Why the researcher's wording was refused: the gate, its length, or no words at all. */
export const BRIEF_WORDING_RULES = ['gate', 'too_long', 'empty'] as const;
export type BriefWordingRule = (typeof BRIEF_WORDING_RULES)[number];

const WORDING_REFUSALS: Record<BriefWordingRule, string> = {
  gate: 'the wording holds a number, a quotation or a character a brief cannot show',
  too_long: `the verdict is held to ${String(MAX_BRIEF_HEADLINE_CHARS)} characters of words`,
  empty: 'the verdict needs some words',
};

const wordingRefused = (rule: BriefWordingRule) =>
  new DomainError('brief_wording_refused', WORDING_REFUSALS[rule], { rule });

const editInvalid = (message: string, detail: Record<string, unknown>) =>
  new DomainError('invalid_content', message, { kind: 'brief', ...detail });

/** What stands in for each stored count while the words are gated: well formed, never resolved. */
const CHIP_PLACEHOLDER = briefToken({ kind: 'of', poll: 1 });

const textLength = (words: readonly string[]): number =>
  words.reduce((total, each) => total + each.length, 0);

/**
 * The headline rebuilt from the researcher's words around its own counts. The words are joined with
 * one placeholder token per stored count and read by `gatedBriefProse`, so they are held to exactly
 * what the synthesis's prose is: no numeral, no quoted passage read across a count, no stray or
 * typed token, no character outside the Brief's. A token the researcher typed is one token too
 * many, and is refused with the rest.
 */
const rewordHeadline = (headline: RichText, wording: readonly string[]): RichText => {
  const chips = richTextChips(headline);
  if (wording.length !== chips.length + 1) {
    throw editInvalid('the wording does not fit this verdict', {
      field: 'wording',
      slots: chips.length + 1,
    });
  }

  const last = wording.length - 1;
  const slots = wording.map((slot, index) => {
    const start = index === 0 ? slot.trimStart() : slot;
    return index === last ? start.trimEnd() : start;
  });
  if (textLength(slots) > MAX_BRIEF_HEADLINE_CHARS) {
    throw wordingRefused('too_long');
  }

  const parts = gatedBriefProse(slots.join(CHIP_PLACEHOLDER));
  if (parts === null || parts.filter((part) => part.t === 'token').length !== chips.length) {
    throw wordingRefused('gate');
  }
  const words = parts.flatMap((part) => (part.t === 'text' ? [part.text] : []));
  if (textLength(words) > MAX_BRIEF_HEADLINE_CHARS) {
    throw wordingRefused('too_long');
  }
  if (!words.some((each) => /\p{L}/u.test(each))) {
    throw wordingRefused('empty');
  }
  // What stood between two counts may not be taken away, or they would run together: words stay
  // words, and a space stays at least a space.
  const stored = headlineWordingSlots(headline);
  const gap = slots.findIndex((slot, index) => {
    const was = stored[index] ?? '';
    return (
      index > 0 &&
      index < last &&
      (was.trim() === '' ? was !== '' && slot === '' : slot.trim() === '')
    );
  });
  if (gap !== -1) {
    throw new DomainError('brief_wording_refused', WORDING_REFUSALS.empty, {
      rule: 'empty',
      slot: gap,
    });
  }

  let chip = 0;
  return parts.map((part): RichTextSegment => {
    if (part.t === 'text') {
      return { t: 'text', text: part.text };
    }
    const segment = chips[chip] as RichTextSegment;
    chip += 1;
    return segment;
  });
};

/** Two headlines with the same counts in the same places and the same words around them. */
const sameWording = (left: RichText, right: RichText): boolean =>
  sameRichText(richTextChips(left), richTextChips(right)) &&
  sameOrder(headlineWordingSlots(left), headlineWordingSlots(right));

const rewordVerdict = (
  verdict: BriefContent['verdict'],
  wording: readonly string[],
): BriefContent['verdict'] => {
  const headline = rewordHeadline(verdict.headline, wording);
  if (sameWording(headline, verdict.headline)) {
    return verdict;
  }
  const sigora = verdict.sigora_headline ?? verdict.headline;

  return sameWording(headline, sigora)
    ? { ...verdict, headline: sigora, sigora_headline: null }
    : { ...verdict, headline, sigora_headline: sigora };
};

const setSummaryVisibility = (
  summary: BriefContent['summary'],
  itemId: string,
  visible: boolean,
): BriefContent['summary'] => {
  const item = summary.find((each) => each.id === itemId);
  if (item === undefined) {
    throw editInvalid('that bullet is not in this summary', { field: 'item_id', item_id: itemId });
  }
  if (item.visible === visible) {
    return summary;
  }
  if (!visible && !summary.some((each) => each.id !== itemId && each.visible)) {
    throw new DomainError('brief_summary_empty', 'a brief shows at least one summary bullet', {
      item_id: itemId,
    });
  }

  return summary.map((each) => (each.id === itemId ? { ...each, visible } : each));
};

const reorderRecommendations = (
  recommendation: BriefContent['recommendation'],
  order: readonly string[],
): BriefContent['recommendation'] => {
  const items = new Map(recommendation.recommendations.map((item) => [item.id, item]));
  if (!isPermutationOf(order, [...items.keys()])) {
    throw editInvalid('the order names every recommendation once', { field: 'order' });
  }
  const sigora = recommendation.sigora_order ?? recommendation.recommendations.map((i) => i.id);

  return {
    ...recommendation,
    recommendations: order.flatMap((id) => items.get(id) ?? []),
    sigora_order: sameOrder(order, sigora) ? null : sigora,
  };
};

/**
 * One framing edit, as the researcher sends it. Nothing in it can carry a count, an option's
 * wording, a quote or any block but the three it names.
 *
 * - `reword_verdict` — the headline's words, one run per slot (`headlineWordingSlots`).
 * - `set_summary_visibility` — show or hide one summary bullet.
 * - `reorder_recommendations` — every recommendation's id, in the order wanted.
 */
export const briefEditSchema = z.discriminatedUnion('op', [
  z.strictObject({
    op: z.literal('reword_verdict'),
    wording: z
      .array(z.string().max(MAX_BRIEF_PROSE_INPUT_CHARS))
      .min(1)
      .max(MAX_RICH_TEXT_SEGMENTS + 1),
  }),
  z.strictObject({
    op: z.literal('set_summary_visibility'),
    item_id: z.string().min(1).max(16),
    visible: z.boolean(),
  }),
  z.strictObject({
    op: z.literal('reorder_recommendations'),
    order: z.array(z.string().min(1).max(16)).min(1).max(MAX_BRIEF_LIST_ITEMS),
  }),
]);
export type BriefEdit = z.infer<typeof briefEditSchema>;

const editedBrief = (content: BriefContent, edit: BriefEdit): BriefContent => {
  switch (edit.op) {
    case 'reword_verdict':
      return { ...content, verdict: rewordVerdict(content.verdict, edit.wording) };
    case 'set_summary_visibility':
      return {
        ...content,
        summary: setSummaryVisibility(content.summary, edit.item_id, edit.visible),
      };
    case 'reorder_recommendations':
      return {
        ...content,
        recommendation: reorderRecommendations(content.recommendation, edit.order),
      };
  }
};

/**
 * The Brief with one framing edit made, and every other key of it as it was. An edit that changes
 * nothing answers the very `content` it was given, and only such an edit does; the store writes no
 * version for it.
 *
 * Refused: `brief_wording_refused` (`detail.rule`) for wording the gate refuses, past
 * `MAX_BRIEF_HEADLINE_CHARS`, with no words, or with none left between two counts that had some
 * (`detail.slot` names that gap); `brief_summary_empty` for hiding the only bullet
 * showing; `invalid_content` for wording with the wrong number of slots, a bullet the summary does
 * not hold, or an order that is not every recommendation once.
 */
export const applyBriefEdit = (content: BriefContent, edit: BriefEdit): BriefContent => {
  const edited = editedBrief(content, edit);

  return canonicalJson(edited) === canonicalJson(content)
    ? content
    : briefContentSchema.parse(edited);
};

/** What a version written by one edit says produced it. */
export const briefEditReason = (edit: BriefEdit): BriefVersionReason => {
  switch (edit.op) {
    case 'reword_verdict':
      return 'reworded_by_you';
    case 'set_summary_visibility':
      return edit.visible ? 'shown_by_you' : 'hidden_by_you';
    case 'reorder_recommendations':
      return 'reordered_by_you';
  }
};

const summaryIdSchema = z.string().regex(/^S[1-9]\d*$/);
const recommendationIdSchema = z.string().regex(/^R[1-9]\d*$/);

/**
 * Where a Sigora revision could not keep the researcher's framing as it was, by code and item id:
 *
 * - `verdict_wording_replaced` — Sigora's headline changed under the researcher's wording, so the
 *   new headline shows and their wording stays in the version before.
 * - `hidden_bullets_removed` — bullets the researcher hid that the revision no longer holds, by
 *   their ids in the version before.
 * - `hidden_bullets_shown` — every bullet left was hidden, so all of them show.
 * - `recommendations_changed_under_your_order` — the researcher's order is kept for what is still
 *   there; `added` follow it in Sigora's order, and `removed` many are gone.
 */
export const briefFramingChangeSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('verdict_wording_replaced') }),
  z.strictObject({
    kind: z.literal('hidden_bullets_removed'),
    previous_ids: z.array(summaryIdSchema).min(1),
  }),
  z.strictObject({ kind: z.literal('hidden_bullets_shown'), ids: z.array(summaryIdSchema).min(1) }),
  z.strictObject({
    kind: z.literal('recommendations_changed_under_your_order'),
    added: z.array(recommendationIdSchema),
    removed: z.int().nonnegative(),
  }),
]);
export type BriefFramingChange = z.infer<typeof briefFramingChangeSchema>;

export const BRIEF_CHANGE_NOTES_SCHEMA_VERSION = 1;

/**
 * What a Sigora revision of a Brief says about itself, stored beside the version as
 * `artifact_versions.change_notes`: its own note, the blocks whose Sigora-written content changed,
 * and where it could not keep the researcher's framing. Written from the same contents as the
 * version, in the same transaction, by the one place that knows why framing was dropped.
 */
export const briefChangeNotesSchema = z.strictObject({
  schema_version: z.literal(BRIEF_CHANGE_NOTES_SCHEMA_VERSION),
  note: richTextSchema.nullable(),
  blocks: z.array(z.enum(BRIEF_BLOCKS)),
  framing: z.array(briefFramingChangeSchema),
});
export type BriefChangeNotes = z.infer<typeof briefChangeNotesSchema>;

/** Stored notes, or `null` for none and for a value this build cannot read. */
export const parseBriefChangeNotes = (value: unknown): BriefChangeNotes | null => {
  const parsed = briefChangeNotesSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};

/** A Brief as Sigora wrote it: every bullet showing, its own headline and its own order. */
export const sigoraFraming = (content: BriefContent): BriefContent => {
  const { recommendation, verdict } = content;
  const items = new Map(recommendation.recommendations.map((item) => [item.id, item]));

  return {
    ...content,
    verdict: {
      ...verdict,
      headline: verdict.sigora_headline ?? verdict.headline,
      sigora_headline: null,
    },
    summary: content.summary.map((item) => ({ ...item, visible: true })),
    recommendation: {
      ...recommendation,
      recommendations:
        recommendation.sigora_order === null
          ? recommendation.recommendations
          : recommendation.sigora_order.flatMap((id) => items.get(id) ?? []),
      sigora_order: null,
    },
  };
};

/**
 * Whether two Briefs' Methods differ, compared canonically. The Method is the accepted inputs' and
 * the evidence's, so no revision may change it.
 */
export const briefMethodChanged = (current: BriefContent, next: BriefContent): boolean =>
  canonicalJson(current.method) !== canonicalJson(next.method);

/** The blocks whose Sigora-written content differs between two Briefs, framing aside. */
export const briefChangedBlocks = (previous: BriefContent, next: BriefContent): BriefBlock[] => {
  const before = sigoraFraming(previous);
  const after = sigoraFraming(next);

  return BRIEF_BLOCKS.filter(
    (block) => canonicalJson(before[block]) !== canonicalJson(after[block]),
  );
};

/**
 * What a passage says, apart from the values its counts and totals were given: its words, and what
 * each count, total and option wording names. A revision that refreshes a count over the same
 * words says the same thing.
 */
export const passageKey = (text: RichText): string =>
  canonicalJson(
    text.map((segment) => {
      switch (segment.t) {
        case 'text':
          return segment;
        case 'count':
          return { t: segment.t, ref: segment.ref };
        case 'of':
          return { t: segment.t, poll: segment.poll };
        case 'option':
          return { t: segment.t, poll: segment.poll, option: segment.option };
      }
    }),
  );

const samePassage = (left: RichText, right: RichText): boolean =>
  passageKey(left) === passageKey(right);

/** `text` with its counts, totals and option wordings taken, in order, from `from`. */
const withChipsOf = (text: RichText, from: RichText): RichText => {
  const chips = richTextChips(from);
  let chip = 0;

  return text.map((segment) => {
    if (segment.t === 'text') {
      return segment;
    }
    const taken = chips[chip] ?? segment;
    chip += 1;
    return taken;
  });
};

/**
 * For each item of `next`, the item of `previous` that says the same thing (`passageKey`), or
 * none. Matched so rather than by id, since a recomposed Brief numbers its items afresh; items
 * saying the same thing are matched in order, and an item whose words or counted things changed is
 * a different item.
 */
const matchByText = <Item extends { text: RichText }>(
  previous: readonly Item[],
  next: readonly Item[],
): (Item | undefined)[] => {
  const byText = new Map<string, Item[]>();
  for (const item of previous) {
    const key = passageKey(item.text);
    byText.set(key, [...(byText.get(key) ?? []), item]);
  }

  return next.map((item) => byText.get(passageKey(item.text))?.shift());
};

/** What a revision becomes once the researcher's framing is carried onto it. */
export interface RebasedBrief {
  content: BriefContent;
  framing: BriefFramingChange[];
  blocks: BriefBlock[];
}

/**
 * A Sigora revision with the researcher's framing carried onto it. `composed` is a fresh
 * composition, framed as Sigora writes it; `current` is the Brief it replaces.
 *
 * Items and headlines are compared by what they say (`passageKey`): their words and what each count
 * names, not the values the counts hold, so a revision that only refreshes a count keeps the
 * framing over it and shows the new value.
 *
 * - The researcher's wording stays, around the revision's counts, while Sigora's headline under it
 *   says the same thing; otherwise the new headline shows (`verdict_wording_replaced`).
 * - A bullet keeps its visibility where the revision still holds it, and a new one shows. A hidden
 *   bullet that is gone is named (`hidden_bullets_removed`); and when no bullet would show, every
 *   one does (`hidden_bullets_shown`).
 * - A researcher's order is kept for the recommendations still there, with new ones after them in
 *   Sigora's order; any added or removed are named (`recommendations_changed_under_your_order`).
 * - Everything else is the revision's.
 *
 * `blocks` are the blocks whose Sigora-written content the revision changed.
 */
export const rebaseBriefFraming = (current: BriefContent, composed: BriefContent): RebasedBrief => {
  const framing: BriefFramingChange[] = [];

  let { verdict } = composed;
  const underneath = current.verdict.sigora_headline;
  if (underneath !== null && samePassage(composed.verdict.headline, underneath)) {
    verdict = {
      ...composed.verdict,
      headline: withChipsOf(current.verdict.headline, composed.verdict.headline),
      sigora_headline: composed.verdict.headline,
    };
  } else if (
    underneath !== null &&
    !samePassage(composed.verdict.headline, current.verdict.headline)
  ) {
    framing.push({ kind: 'verdict_wording_replaced' });
  }

  const bullets = matchByText(current.summary, composed.summary);
  let summary = composed.summary.map((item, index) => ({
    ...item,
    visible: bullets[index]?.visible ?? true,
  }));
  const kept = new Set(bullets);
  const removed = current.summary.filter((item) => !item.visible && !kept.has(item));
  if (removed.length > 0) {
    framing.push({ kind: 'hidden_bullets_removed', previous_ids: removed.map((item) => item.id) });
  }
  if (!summary.some((item) => item.visible)) {
    framing.push({ kind: 'hidden_bullets_shown', ids: summary.map((item) => item.id) });
    summary = summary.map((item) => ({ ...item, visible: true }));
  }

  let { recommendation } = composed;
  if (current.recommendation.sigora_order !== null) {
    const previous = current.recommendation.recommendations;
    const matches = matchByText(previous, composed.recommendation.recommendations);
    const at = (index: number) => previous.indexOf(matches[index] as (typeof previous)[number]);
    const still = composed.recommendation.recommendations
      .map((item, index) => ({ item, index }))
      .filter(({ index }) => matches[index] !== undefined)
      .toSorted((left, right) => at(left.index) - at(right.index))
      .map(({ item }) => item);
    const added = composed.recommendation.recommendations.filter(
      (_, index) => matches[index] === undefined,
    );
    const ordered = [...still, ...added];
    const sigora = composed.recommendation.recommendations.map((item) => item.id);
    recommendation = {
      ...composed.recommendation,
      recommendations: ordered,
      sigora_order: sameOrder(
        ordered.map((item) => item.id),
        sigora,
      )
        ? null
        : sigora,
    };
    if (added.length > 0 || still.length < previous.length) {
      framing.push({
        kind: 'recommendations_changed_under_your_order',
        added: added.map((item) => item.id),
        removed: previous.length - still.length,
      });
    }
  }

  return {
    content: briefContentSchema.parse({ ...composed, verdict, summary, recommendation }),
    framing,
    blocks: briefChangedBlocks(current, composed),
  };
};

/** Which blocks show the researcher's framing: "Edited by you", or "Reordered by you". */
export interface BriefFramingMarkers {
  verdict: boolean;
  summary: boolean;
  recommendation: boolean;
}

export const briefFramingMarkers = (content: BriefContent): BriefFramingMarkers => ({
  verdict: content.verdict.sigora_headline !== null,
  summary: content.summary.some((item) => !item.visible),
  recommendation: content.recommendation.sigora_order !== null,
});

/**
 * The line a Brief version's reason reads as, on the version line and in the history. The
 * publication carries no reason and reads as Sigora's; so does a reason this build does not know,
 * by whoever wrote it. Placeholder copy.
 */
export const briefVersionReasonText = ({
  reason,
  authoredBy,
}: {
  reason: ArtifactVersionReason | null;
  authoredBy: AuthorKind;
}): string => {
  switch (reason) {
    case 'reworded_by_you':
      return 'Verdict reworded by you';
    case 'hidden_by_you':
      return 'A summary bullet hidden by you';
    case 'shown_by_you':
      return 'A summary bullet shown again by you';
    case 'reordered_by_you':
      return 'Recommendations reordered by you';
    case 'revised_by_sigora':
      return 'Updated by Sigora';
    case 'updated_from_follow_up':
      return 'Updated after a follow-up question';
    default:
      return authoredBy === 'user' ? 'Edited by you' : 'Written by Sigora';
  }
};
